<template>
  <section class="english-portal">
    <header class="hero-card">
      <div class="hero-copy">
        <p class="hero-eyebrow">{{ t("eyebrow") }}</p>
        <h1 class="hero-title">{{ t("title") }}</h1>
        <p class="hero-lead">{{ t("lead") }}</p>
      </div>

      <div class="hero-tools">
        <div class="lang-pill">
          <button
            v-for="choice in langs"
            :key="choice"
            type="button"
            :class="['lang-chip', { active: choice === lang }]"
            @click="setLang(choice)"
          >
            {{ choice.toUpperCase() }}
          </button>
        </div>

        <div class="study-digest panel-card">
          <span class="panel-tag">{{ t("digestTag") }}</span>
          <h2 class="digest-title">{{ t("digestTitle") }}</h2>
          <p class="digest-copy">{{ t("digestCopy") }}</p>
          <div class="digest-grid">
            <article class="digest-tile">
              <span>{{ t("todayWords") }}</span>
              <strong>{{ dashboard.todayWordCount }}</strong>
            </article>
            <article class="digest-tile">
              <span>{{ t("favorites") }}</span>
              <strong>{{ dashboard.favoriteWordCount }}</strong>
            </article>
            <article class="digest-tile">
              <span>{{ t("mastered") }}</span>
              <strong>{{ dashboard.masteredWordCount }}</strong>
            </article>
            <article class="digest-tile">
              <span>{{ t("viewedRoots") }}</span>
              <strong>{{ dashboard.viewedRootCount }}</strong>
            </article>
          </div>
        </div>
      </div>
    </header>

    <section class="portal-grid">
      <article class="panel-card portal-card">
        <div class="section-head">
          <span class="panel-tag">{{ t("portalTag") }}</span>
          <h2 class="section-title">{{ loading ? t("loading") : t("portalTitle") }}</h2>
          <p class="section-copy">{{ loading ? t("loadingPortalCopy") : t("portalCopy") }}</p>
        </div>

        <div class="summary-grid">
          <article class="summary-tile">
            <span>{{ t("rootStat") }}</span>
            <strong>{{ loading ? "-" : portal.summary.root_count }}</strong>
          </article>
          <article class="summary-tile">
            <span>{{ t("wordStat") }}</span>
            <strong>{{ loading ? "-" : portal.summary.word_count }}</strong>
          </article>
          <article class="summary-tile">
            <span>{{ t("videoRoots") }}</span>
            <strong>{{ loading ? "-" : portal.summary.roots_with_video_count }}</strong>
          </article>
          <article class="summary-tile">
            <span>{{ t("videoWords") }}</span>
            <strong>{{ loading ? "-" : portal.summary.words_with_video_count }}</strong>
          </article>
        </div>
      </article>

      <article class="panel-card practice-card">
        <div class="section-head compact-head">
          <span class="panel-tag">{{ t("practiceTag") }}</span>
          <h2 class="section-title">{{ t("practiceTitle") }}</h2>
          <p class="section-copy">{{ t("practiceCopy") }}</p>
        </div>

        <div class="practice-actions">
          <button type="button" class="action-btn" @click="goPractice">
            {{ t("openPractice") }}
          </button>
          <button type="button" class="action-btn secondary" @click="resumePractice">
            {{ t("resumePractice") }}
          </button>
        </div>

        <div class="practice-root-list">
          <button
            v-for="root in portal.practice_roots"
            :key="root.root_id"
            type="button"
            class="practice-root-chip"
            @click="openRoot(root)"
          >
            <strong>{{ root.root_text }}</strong>
            <span>{{ getRootMeaning(root, lang) }}</span>
          </button>
        </div>
      </article>
    </section>

    <section class="content-grid">
      <article class="panel-card recent-card">
        <div class="section-head compact-head">
          <span class="panel-tag">{{ t("recentTag") }}</span>
          <h2 class="section-title">{{ t("recentTitle") }}</h2>
          <p class="section-copy">{{ t("recentCopy") }}</p>
        </div>

        <div v-if="dashboard.recentWords.length || dashboard.recentRoots.length" class="recent-columns">
          <div class="recent-column">
            <h3>{{ t("recentWordsTitle") }}</h3>
            <button
              v-for="word in dashboard.recentWords"
              :key="word.wordId"
              type="button"
              class="recent-item"
              @click="openRecentWord(word)"
            >
              <div>
                <strong>{{ word.wordText }}</strong>
                <p>{{ word.meaning }}</p>
              </div>
              <span>{{ word.rootId }}</span>
            </button>
          </div>

          <div class="recent-column">
            <h3>{{ t("recentRootsTitle") }}</h3>
            <button
              v-for="root in dashboard.recentRoots"
              :key="root.rootId"
              type="button"
              class="recent-item"
              @click="openRecentRoot(root)"
            >
              <div>
                <strong>{{ root.rootText }}</strong>
                <p>{{ root.meaning }}</p>
              </div>
              <span>{{ root.kind || "-" }}</span>
            </button>
          </div>
        </div>
        <p v-else class="empty-state">{{ t("noRecent") }}</p>
      </article>

      <article class="panel-card search-card">
        <div class="section-head compact-head">
          <span class="panel-tag">{{ t("searchTag") }}</span>
          <h2 class="section-title">{{ t("searchTitle") }}</h2>
          <p class="section-copy">{{ t("searchCopy") }}</p>
        </div>

        <div class="search-panels">
          <div class="search-block">
            <label class="search-label">{{ t("rootSearch") }}</label>
            <div class="search-row">
              <input
                v-model.trim="rootKeyword"
                type="text"
                :placeholder="t('rootPlaceholder')"
                @keyup.enter="searchRoots"
              />
              <button type="button" :disabled="rootLoading" @click="searchRoots">
                {{ rootLoading ? t("searching") : t("search") }}
              </button>
            </div>
            <p v-if="rootErrorMessage" class="error-text">{{ rootErrorMessage }}</p>
            <div class="search-results">
              <button
                v-for="root in roots"
                :key="root.root_id"
                type="button"
                class="search-result-card"
                @click="openRoot(root)"
              >
                <strong>{{ root.root_text }}</strong>
                <p>{{ getRootMeaning(root, lang) }}</p>
                <span>{{ root.root_id }}</span>
              </button>
            </div>
            <p v-if="rootHasSearched && !rootLoading && roots.length === 0" class="empty-state">
              {{ t("noResult") }}
            </p>
          </div>

          <div class="search-block">
            <label class="search-label">{{ t("wordSearch") }}</label>
            <div class="search-row">
              <input
                v-model.trim="wordKeyword"
                type="text"
                :placeholder="t('wordPlaceholder')"
                @keyup.enter="searchWords"
              />
              <button type="button" :disabled="wordLoading" @click="searchWords">
                {{ wordLoading ? t("searching") : t("search") }}
              </button>
            </div>
            <p v-if="wordErrorMessage" class="error-text">{{ wordErrorMessage }}</p>
            <div class="search-results">
              <button
                v-for="word in words"
                :key="word.word_id"
                type="button"
                class="search-result-card"
                @click="openWord(word)"
              >
                <strong>{{ word.word_text }}</strong>
                <p>{{ getWordMeaning(word, lang) }}</p>
                <span>{{ word.root_id }}</span>
              </button>
            </div>
            <p v-if="wordHasSearched && !wordLoading && words.length === 0" class="empty-state">
              {{ t("noResult") }}
            </p>
          </div>
        </div>
      </article>
    </section>

    <section class="content-grid">
      <article class="panel-card">
        <div class="section-head compact-head">
          <span class="panel-tag">{{ t("featuredRootsTag") }}</span>
          <h2 class="section-title">{{ t("featuredRootsTitle") }}</h2>
          <p class="section-copy">{{ t("featuredRootsCopy") }}</p>
        </div>

        <div class="card-grid">
          <button
            v-for="root in portal.featured_roots"
            :key="root.root_id"
            type="button"
            class="entity-card"
            @click="openRoot(root)"
          >
            <div class="entity-head">
              <strong>{{ root.root_text }}</strong>
              <span>{{ root.kind }}</span>
            </div>
            <p>{{ getRootMeaning(root, lang) }}</p>
            <small>{{ root.root_id }}</small>
          </button>
        </div>
      </article>

      <article class="panel-card">
        <div class="section-head compact-head">
          <span class="panel-tag">{{ t("featuredWordsTag") }}</span>
          <h2 class="section-title">{{ t("featuredWordsTitle") }}</h2>
          <p class="section-copy">{{ t("featuredWordsCopy") }}</p>
        </div>

        <div class="card-grid">
          <button
            v-for="word in portal.featured_words"
            :key="word.word_id"
            type="button"
            class="entity-card"
            @click="openWord(word)"
          >
            <div class="entity-head">
              <strong>{{ word.word_text }}</strong>
              <span>{{ word.root_id }}</span>
            </div>
            <p>{{ getWordMeaning(word, lang) }}</p>
            <small>{{ getPronunciation(word) }}</small>
          </button>
        </div>
      </article>
    </section>

    <section class="panel-card latest-card">
      <div class="section-head compact-head">
        <span class="panel-tag">{{ t("latestTag") }}</span>
        <h2 class="section-title">{{ t("latestTitle") }}</h2>
        <p class="section-copy">{{ t("latestCopy") }}</p>
      </div>

      <div class="latest-list">
        <button
          v-for="word in portal.latest_words"
          :key="word.word_id"
          type="button"
          class="latest-item"
          @click="openWord(word)"
        >
          <div>
            <strong>{{ word.word_text }}</strong>
            <p>{{ getWordMeaning(word, lang) }}</p>
          </div>
          <span>{{ formatDate(word.updated_at) }}</span>
        </button>
      </div>
    </section>

    <article v-if="errorMessage" class="panel-card error-card">
      <span class="panel-tag">{{ t("errorTag") }}</span>
      <h2 class="section-title">{{ t("errorTitle") }}</h2>
      <p class="section-copy">{{ errorMessage }}</p>
    </article>
  </section>
</template>

<script setup>
import axios from "axios";
import { onMounted, ref } from "vue";
import { useRouter } from "vue-router";
import { useSiteLanguage } from "../../store/siteLanguage";
import {
  API_BASE,
  ensureApiArray,
  ensureApiObject,
  formatDate,
  getPronunciation,
  getRootMeaning,
  getStudyDashboard,
  getWordMeaning,
} from "./englishStudyShared";

const router = useRouter();
const { lang, languages: langs, setLang } = useSiteLanguage();

const loading = ref(true);
const errorMessage = ref("");
const portal = ref({
  summary: {
    root_count: 0,
    word_count: 0,
    roots_with_video_count: 0,
    words_with_video_count: 0,
  },
  featured_roots: [],
  featured_words: [],
  latest_words: [],
  practice_roots: [],
});

const dashboard = ref(getStudyDashboard());

const rootKeyword = ref("");
const roots = ref([]);
const rootLoading = ref(false);
const rootErrorMessage = ref("");
const rootHasSearched = ref(false);

const wordKeyword = ref("");
const words = ref([]);
const wordLoading = ref(false);
const wordErrorMessage = ref("");
const wordHasSearched = ref(false);

const i18n = {
  ja: {
    eyebrow: "ITKENSHI ENGLISH ROOT LAB",
    title: "英語語根学習ポータル",
    lead: "語根、単語、練習、最近の学習履歴を一つにまとめた英語学習の操作台です。",
    digestTag: "STUDY DIGEST",
    digestTitle: "学習ダイジェスト",
    digestCopy: "見た単語、お気に入り、習得済み、今日触れた語根をこの端末に記録します。",
    todayWords: "今日の単語",
    favorites: "お気に入り",
    mastered: "習得済み",
    viewedRoots: "見た語根",
    portalTag: "PORTAL",
    portalTitle: "学習の入口",
    portalCopy: "おすすめ語根や単語から入り、詳細ページで構造と語族をじっくり確認できます。",
    loading: "Loading...",
    loadingPortalCopy: "語根と単語データを読み込んでいます。",
    rootStat: "語根数",
    wordStat: "単語数",
    videoRoots: "動画あり語根",
    videoWords: "動画あり単語",
    practiceTag: "PRACTICE",
    practiceTitle: "すぐ練習",
    practiceCopy: "選択式でも入力式でも、今の気分に合わせてすぐ始められます。",
    openPractice: "練習モードを開く",
    resumePractice: "前回の設定で再開",
    recentTag: "RECENT STUDY",
    recentTitle: "最近の学習",
    recentCopy: "直近で開いた単語と語根をそのまま再開できます。",
    recentWordsTitle: "最近見た単語",
    recentRootsTitle: "最近見た語根",
    noRecent: "最近の学習履歴はまだありません。",
    searchTag: "SEARCH DESK",
    searchTitle: "検索デスク",
    searchCopy: "語根と単語をその場で探し、詳細ページへすぐ移動できます。",
    rootSearch: "語根検索",
    wordSearch: "単語検索",
    rootPlaceholder: "act / ag / ER00000001",
    wordPlaceholder: "act / action / EW00010001",
    search: "検索",
    searching: "検索中",
    noResult: "結果が見つかりませんでした。",
    featuredRootsTag: "ROOT RADAR",
    featuredRootsTitle: "おすすめ語根",
    featuredRootsCopy: "学習を始めやすい語根から順に見ていけます。",
    featuredWordsTag: "WORD RADAR",
    featuredWordsTitle: "おすすめ単語",
    featuredWordsCopy: "動画付きや最近更新された単語を先に拾えます。",
    latestTag: "LATEST UPDATES",
    latestTitle: "最近更新された単語",
    latestCopy: "新しく入った単語や更新された単語を時系列で確認できます。",
    errorTag: "STATUS",
    errorTitle: "データの読み込みに失敗しました",
    rootError: "語根検索に失敗しました。",
    wordError: "単語検索に失敗しました。",
  },
  zh: {
    eyebrow: "ITKENSHI ENGLISH ROOT LAB",
    title: "英语语根学习门户",
    lead: "把语根、单词、练习和最近学习记录放在一起，作为一站式英语学习控制台。",
    digestTag: "STUDY DIGEST",
    digestTitle: "学习摘要",
    digestCopy: "本地保存你今天学过的单词、收藏、已掌握状态和最近浏览的词根。",
    todayWords: "今日单词",
    favorites: "收藏",
    mastered: "已掌握",
    viewedRoots: "已看词根",
    portalTag: "PORTAL",
    portalTitle: "学习入口",
    portalCopy: "可以从推荐词根或推荐单词进入，再在详情页里继续看结构拆解和同词根词族。",
    loading: "Loading...",
    loadingPortalCopy: "正在读取语根和单词数据。",
    rootStat: "词根数",
    wordStat: "单词数",
    videoRoots: "有视频词根",
    videoWords: "有视频单词",
    practiceTag: "PRACTICE",
    practiceTitle: "快速练习",
    practiceCopy: "无论是选择题还是输入题，都可以立刻开始。",
    openPractice: "打开练习",
    resumePractice: "按上次设置继续",
    recentTag: "RECENT STUDY",
    recentTitle: "最近学习",
    recentCopy: "最近看过的单词和词根会保留在这里，方便接着学。",
    recentWordsTitle: "最近单词",
    recentRootsTitle: "最近词根",
    noRecent: "还没有最近学习记录。",
    searchTag: "SEARCH DESK",
    searchTitle: "搜索台",
    searchCopy: "可以直接搜索词根和单词，再跳到详情页继续学习。",
    rootSearch: "词根搜索",
    wordSearch: "单词搜索",
    rootPlaceholder: "act / ag / ER00000001",
    wordPlaceholder: "act / action / EW00010001",
    search: "搜索",
    searching: "搜索中",
    noResult: "没有找到结果。",
    featuredRootsTag: "ROOT RADAR",
    featuredRootsTitle: "推荐词根",
    featuredRootsCopy: "先从更容易建立感觉的词根开始。",
    featuredWordsTag: "WORD RADAR",
    featuredWordsTitle: "推荐单词",
    featuredWordsCopy: "优先展示带视频或最近更新过的单词。",
    latestTag: "LATEST UPDATES",
    latestTitle: "最近更新单词",
    latestCopy: "按时间查看新加入或最近补充过的单词。",
    errorTag: "STATUS",
    errorTitle: "读取数据失败",
    rootError: "词根搜索失败。",
    wordError: "单词搜索失败。",
  },
  en: {
    eyebrow: "ITKENSHI ENGLISH ROOT LAB",
    title: "English Root Study Portal",
    lead: "A single learning desk for roots, words, practice, and recent study history.",
    digestTag: "STUDY DIGEST",
    digestTitle: "Learning Pulse",
    digestCopy: "This device keeps your viewed, favorite, and mastered words locally.",
    todayWords: "Today",
    favorites: "Favorites",
    mastered: "Mastered",
    viewedRoots: "Viewed Roots",
    portalTag: "PORTAL",
    portalTitle: "Where to Start",
    portalCopy: "Start with recommended roots or words, then dive into detail pages for structure and word-family study.",
    loading: "Loading...",
    loadingPortalCopy: "Loading root and word data.",
    rootStat: "Roots",
    wordStat: "Words",
    videoRoots: "Roots with Video",
    videoWords: "Words with Video",
    practiceTag: "PRACTICE",
    practiceTitle: "Quick Practice",
    practiceCopy: "Jump into multiple-choice or typing practice instantly.",
    openPractice: "Open Practice",
    resumePractice: "Resume Last Setup",
    recentTag: "RECENT STUDY",
    recentTitle: "Recent Study",
    recentCopy: "Pick up from the words and roots you opened most recently.",
    recentWordsTitle: "Recent Words",
    recentRootsTitle: "Recent Roots",
    noRecent: "No recent study history yet.",
    searchTag: "SEARCH DESK",
    searchTitle: "Search",
    searchCopy: "Search roots and words, then open their detail pages.",
    rootSearch: "Root Search",
    wordSearch: "Word Search",
    rootPlaceholder: "act / ag / ER00000001",
    wordPlaceholder: "act / action / EW00010001",
    search: "Search",
    searching: "Searching",
    noResult: "No result found.",
    featuredRootsTag: "ROOT RADAR",
    featuredRootsTitle: "Recommended Roots",
    featuredRootsCopy: "Start with roots that are easy to build around.",
    featuredWordsTag: "WORD RADAR",
    featuredWordsTitle: "Recommended Words",
    featuredWordsCopy: "Words with videos or recent updates surface first.",
    latestTag: "LATEST UPDATES",
    latestTitle: "Recently Updated Words",
    latestCopy: "Review new or recently updated words in time order.",
    errorTag: "STATUS",
    errorTitle: "Unable to load data",
    rootError: "Failed to search roots.",
    wordError: "Failed to search words.",
  },
};

function t(key) {
  return i18n[lang.value]?.[key] || key;
}

async function loadPortal() {
  loading.value = true;
  errorMessage.value = "";
  try {
    const response = await axios.get(`${API_BASE}/api/english/portal`, { timeout: 10000 });
    portal.value = ensureApiObject(response, "English portal API");
  } catch (error) {
    console.error(error);
    errorMessage.value = error?.message || t("errorTitle");
  } finally {
    loading.value = false;
  }
}

async function searchRoots() {
  rootHasSearched.value = true;
  rootErrorMessage.value = "";
  if (!rootKeyword.value) {
    roots.value = [];
    return;
  }

  rootLoading.value = true;
  try {
    const response = await axios.get(`${API_BASE}/api/root/search`, {
      params: { keyword: rootKeyword.value },
      timeout: 8000,
    });
    roots.value = ensureApiArray(response, "Root search API");
  } catch (error) {
    console.error(error);
    roots.value = [];
    rootErrorMessage.value = error?.message || t("rootError");
  } finally {
    rootLoading.value = false;
  }
}

async function searchWords() {
  wordHasSearched.value = true;
  wordErrorMessage.value = "";
  if (!wordKeyword.value) {
    words.value = [];
    return;
  }

  wordLoading.value = true;
  try {
    const response = await axios.get(`${API_BASE}/api/word/search`, {
      params: { keyword: wordKeyword.value },
      timeout: 8000,
    });
    words.value = ensureApiArray(response, "Word search API");
  } catch (error) {
    console.error(error);
    words.value = [];
    wordErrorMessage.value = error?.message || t("wordError");
  } finally {
    wordLoading.value = false;
  }
}

function refreshDashboard() {
  dashboard.value = getStudyDashboard();
}

function openRoot(root) {
  router.push(`/english/root/${encodeURIComponent(root.root_id)}`);
}

function openWord(word) {
  router.push(`/english/word/${encodeURIComponent(word.word_id)}`);
}

function openRecentWord(word) {
  router.push(`/english/word/${encodeURIComponent(word.wordId)}`);
}

function openRecentRoot(root) {
  router.push(`/english/root/${encodeURIComponent(root.rootId)}`);
}

function goPractice() {
  router.push("/english/practice");
}

function resumePractice() {
  router.push("/english/practice");
}

onMounted(async () => {
  refreshDashboard();
  await loadPortal();
});
</script>

<style scoped>
.english-portal {
  width: 100%;
  min-height: calc(100vh - 86px);
  padding: 18px;
  background:
    radial-gradient(circle at top left, rgba(14, 165, 233, 0.12), transparent 32%),
    radial-gradient(circle at top right, rgba(234, 179, 8, 0.12), transparent 28%),
    linear-gradient(180deg, #f5f8ff 0%, #eef4fb 100%);
  color: #163047;
  box-sizing: border-box;
}

.hero-card,
.panel-card {
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 28px;
  background: rgba(255, 255, 255, 0.88);
  box-shadow: 0 18px 50px rgba(34, 73, 112, 0.08);
  backdrop-filter: blur(12px);
}

.hero-card {
  display: grid;
  grid-template-columns: 1.45fr 0.9fr;
  gap: 18px;
  padding: 28px;
}

.hero-eyebrow,
.panel-tag {
  margin: 0 0 10px;
  font-size: 12px;
  letter-spacing: 0.24em;
  text-transform: uppercase;
  color: #35648e;
}

.hero-title {
  margin: 0;
  font-size: clamp(40px, 5vw, 72px);
  line-height: 0.95;
  color: #10253a;
}

.hero-lead,
.section-copy,
.digest-copy,
.entity-card p,
.search-result-card p,
.recent-item p,
.latest-item p {
  margin: 0;
  color: #4c6377;
  line-height: 1.7;
}

.hero-tools {
  display: grid;
  gap: 16px;
  align-content: start;
}

.lang-pill {
  justify-self: end;
  display: inline-flex;
  gap: 8px;
  padding: 8px;
  border-radius: 999px;
  background: rgba(255, 255, 255, 0.82);
  border: 1px solid rgba(43, 90, 132, 0.14);
}

.lang-chip,
.action-btn,
.search-row button,
.practice-root-chip,
.entity-card,
.recent-item,
.latest-item,
.search-result-card {
  cursor: pointer;
  transition: transform 180ms ease, box-shadow 180ms ease, border-color 180ms ease, background 180ms ease;
}

.lang-chip {
  border: 0;
  min-width: 56px;
  padding: 10px 14px;
  border-radius: 999px;
  background: transparent;
  color: #47647d;
}

.lang-chip.active {
  background: linear-gradient(135deg, #0f4f8c, #1e7fc7);
  color: #fff;
  box-shadow: 0 10px 24px rgba(30, 127, 199, 0.28);
}

.study-digest {
  padding: 18px;
}

.digest-title,
.section-title {
  margin: 0;
  font-size: clamp(28px, 3.3vw, 44px);
  color: #10253a;
}

.digest-grid,
.summary-grid {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 12px;
  margin-top: 18px;
}

.digest-tile,
.summary-tile {
  border-radius: 20px;
  background: #f3f8fe;
  border: 1px solid rgba(43, 90, 132, 0.12);
  padding: 14px;
}

.digest-tile span,
.summary-tile span {
  display: block;
  font-size: 13px;
  color: #4a6580;
}

.digest-tile strong,
.summary-tile strong {
  display: block;
  margin-top: 10px;
  font-size: 28px;
  color: #0f2840;
}

.portal-grid,
.content-grid {
  display: grid;
  grid-template-columns: 1.2fr 1fr;
  gap: 18px;
  margin-top: 18px;
}

.panel-card {
  padding: 22px;
}

.section-head {
  display: grid;
  gap: 10px;
  margin-bottom: 18px;
}

.compact-head .section-title {
  font-size: clamp(24px, 2.6vw, 36px);
}

.practice-actions {
  display: flex;
  gap: 12px;
  flex-wrap: wrap;
}

.action-btn,
.search-row button {
  border: 0;
  border-radius: 14px;
  padding: 12px 18px;
  font-weight: 700;
  background: linear-gradient(135deg, #0f4f8c, #1e7fc7);
  color: #fff;
}

.action-btn.secondary {
  background: #dceaf8;
  color: #133251;
}

.practice-root-list,
.card-grid,
.latest-list,
.search-results {
  display: grid;
  gap: 12px;
}

.practice-root-list {
  margin-top: 16px;
  grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
}

.practice-root-chip {
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 18px;
  padding: 14px;
  text-align: left;
  background: #f6fbff;
}

.practice-root-chip strong,
.entity-head strong,
.recent-item strong,
.latest-item strong,
.search-result-card strong {
  color: #10253a;
}

.practice-root-chip span,
.entity-head span,
.recent-item span,
.latest-item span,
.search-result-card span {
  color: #5a7288;
}

.recent-columns,
.search-panels {
  display: grid;
  grid-template-columns: repeat(2, minmax(0, 1fr));
  gap: 14px;
}

.recent-column,
.search-block {
  display: grid;
  gap: 10px;
}

.recent-column h3,
.search-label {
  margin: 0;
  font-size: 15px;
  color: #224668;
}

.recent-item,
.latest-item,
.search-result-card,
.entity-card {
  border: 1px solid rgba(43, 90, 132, 0.14);
  border-radius: 20px;
  background: #fbfdff;
  padding: 16px;
  text-align: left;
}

.recent-item,
.latest-item {
  display: flex;
  justify-content: space-between;
  gap: 12px;
}

.search-row {
  display: grid;
  grid-template-columns: 1fr auto;
  gap: 10px;
}

.search-row input {
  width: 100%;
  border: 1px solid rgba(43, 90, 132, 0.18);
  border-radius: 14px;
  background: #fbfdff;
  padding: 12px 14px;
  font-size: 16px;
  color: #12324f;
  box-sizing: border-box;
}

.entity-head {
  display: flex;
  justify-content: space-between;
  gap: 10px;
  align-items: baseline;
  margin-bottom: 8px;
}

.latest-card {
  margin-top: 18px;
}

.empty-state,
.error-text {
  margin: 0;
  font-size: 14px;
}

.error-text {
  color: #b42318;
}

.error-card {
  margin-top: 18px;
  border-color: rgba(180, 35, 24, 0.18);
  background: rgba(255, 244, 242, 0.94);
}

.lang-chip:hover,
.action-btn:hover,
.search-row button:hover,
.practice-root-chip:hover,
.entity-card:hover,
.recent-item:hover,
.latest-item:hover,
.search-result-card:hover {
  transform: translateY(-2px);
  box-shadow: 0 12px 24px rgba(25, 79, 121, 0.12);
}

@media (max-width: 1100px) {
  .hero-card,
  .portal-grid,
  .content-grid,
  .recent-columns,
  .search-panels {
    grid-template-columns: 1fr;
  }

  .lang-pill {
    justify-self: start;
  }
}

@media (max-width: 768px) {
  .english-portal {
    padding: 12px;
  }

  .hero-card,
  .panel-card {
    border-radius: 22px;
    padding: 18px;
  }

  .digest-grid,
  .summary-grid {
    grid-template-columns: 1fr 1fr;
  }

  .recent-item,
  .latest-item,
  .entity-head {
    grid-template-columns: 1fr;
  }

  .recent-item,
  .latest-item {
    flex-direction: column;
  }
}
</style>
